import { IonButton, IonButtons, IonCheckbox, IonContent, IonHeader, IonItem, IonLabel, IonList, IonSearchbar, IonTitle, IonToolbar } from '@ionic/react';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { GolfAvatar } from '../ui/GolfAvatar';
import { messagingService, type User } from '@/services/messagingService';

import { supabase } from '@/utils/supabase';

type NewChatViewProps = {
  userId: string;
  closeNewChat: () => void;
  setActiveChat: React.Dispatch<React.SetStateAction<string | null>>;
};

export default function NewChatViewSupabase({ userId, closeNewChat, setActiveChat }: NewChatViewProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Fetch all users except the current user
        const { data, error } = await supabase
          .from('profiles')
          .select('userId, first_name, last_name, imageUrl, username')
          .neq('userId', userId);

        if (error) throw error;

        if (data) {
          const userList = data.map(user => ({
            userId: user.userId,
            firstName: user.first_name,
            lastName: user.last_name,
            imageUrl: user.imageUrl,
            username: user.username,
          }));
          setUsers(userList);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, [userId]);

  const handleCreateChat = async () => {
    if (selectedUsers.length === 0) return;

    setIsCreatingChat(true);

    try {
      // For simplicity, we're just creating a direct chat with the first selected user
      // In a real app, you might want to handle group chats differently
      const chatId = await messagingService.createChat(
        userId,
        selectedUsers,
        selectedUsers.length > 1 ? 'group' : 'direct',
        selectedUsers.length > 1 ? 'Group Chat' : undefined
      );

      if (chatId) {
        // Navigate to the chat view
        history.push(`/messages/${chatId}`);
        closeNewChat();
      }
    } catch (error) {
      console.error('Error creating chat:', error);
    } finally {
      setIsCreatingChat(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    console.log('Toggling user selection:', userId);
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.username || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="flex size-full flex-col">
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={closeNewChat}>
              <ArrowLeft className="size-5" />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('new_chat')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={selectedUsers.length === 0 || isCreatingChat}
              onClick={handleCreateChat}
              strong={true}
            >
              {t('start')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="p-2">
          <IonSearchbar
            value={searchTerm}
            onIonInput={(e) => setSearchTerm(e.detail.value || '')}
            placeholder={t('search_players')}
            className="ion-no-padding"
          />
        </div>

        <IonList>
          {filteredUsers.map((user) => (
            <IonItem key={user.userId}>
              <GolfAvatar
                imageUrl={user.imageUrl}
                name={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Unknown'}
                size={40}
                className="mr-2"
              />
              <IonLabel>
                <h2>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Unknown User3'}</h2>
                <p className="text-muted-foreground">@{user.username || ''}</p>
              </IonLabel>
              <IonCheckbox
                slot="end"
                checked={selectedUsers.includes(user.userId)}
                onIonChange={() => toggleUserSelection(user.userId)}
              />
            </IonItem>
          ))}

          {filteredUsers.length === 0 && (
            <div className="p-4 text-center">
              <p className="text-muted-foreground">{t('no_users_found')}</p>
            </div>
          )}
        </IonList>
      </IonContent>
    </div>
  );
}
